import { useState } from "react"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/modules/shared/ui/components/sheet"
import { Separator } from "@/modules/shared/ui/components/separator"
import { PermissionsTable } from "./PermissionsTable"
import { GrantPermissionForm } from "./GrantPermissionForm"
import { BulkGrantForm } from "./BulkGrantForm"
import {
  useDocumentPermissions,
  useGrantDocumentPermission,
  useRevokeDocumentPermission,
  useBulkGrantDocumentPermissions,
} from "../../hooks/useDocumentPermissions"
import {
  useFolderPermissions,
  useGrantFolderPermission,
  useRevokeFolderPermission,
  useBulkGrantFolderPermissions,
} from "../../hooks/useFolderPermissions"
import { BulkShareResult } from "../../dtos/BulkPermissionDtos"
import { GrantDocumentPermissionRequest } from "../../dtos/DocumentPermissionDto"
import { GrantFolderPermissionRequest } from "../../dtos/FolderPermissionDto"

interface PermissionPanelProps {
  open: boolean
  onClose: () => void
  type: "document" | "folder"
  targetId: string
  targetName: string
}

function DocumentPermissionPanel({
  targetId,
}: Omit<PermissionPanelProps, "open" | "type">) {
  const [bulkResult, setBulkResult] = useState<BulkShareResult | null>(null)

  const { data: permissions = [], isLoading } = useDocumentPermissions(targetId)
  const grantMutation = useGrantDocumentPermission(targetId)
  const revokeMutation = useRevokeDocumentPermission(targetId)
  const bulkGrantMutation = useBulkGrantDocumentPermissions(targetId)

  const handleGrant = (request: GrantDocumentPermissionRequest) => {
    grantMutation.mutate(request)
  }

  const handleBulkGrant = (
    request: Parameters<typeof bulkGrantMutation.mutate>[0],
  ) => {
    bulkGrantMutation.mutate(request, {
      onSuccess: (result) => setBulkResult(result),
    })
  }

  return (
    <PanelContent
      permissions={permissions}
      isLoading={isLoading}
      onRevoke={(id) => revokeMutation.mutate(id)}
      isRevoking={revokeMutation.isPending}
      onGrant={handleGrant}
      isGranting={grantMutation.isPending}
      onBulkGrant={handleBulkGrant}
      isBulkGranting={bulkGrantMutation.isPending}
      bulkResult={bulkResult}
    />
  )
}

function FolderPermissionPanel({
  targetId,
}: Omit<PermissionPanelProps, "open" | "type">) {
  const [bulkResult, setBulkResult] = useState<BulkShareResult | null>(null)

  const { data: permissions = [], isLoading } = useFolderPermissions(targetId)
  const grantMutation = useGrantFolderPermission(targetId)
  const revokeMutation = useRevokeFolderPermission(targetId)
  const bulkGrantMutation = useBulkGrantFolderPermissions(targetId)

  const handleGrant = (request: GrantFolderPermissionRequest) => {
    grantMutation.mutate(request)
  }

  const handleBulkGrant = (
    request: Parameters<typeof bulkGrantMutation.mutate>[0],
  ) => {
    bulkGrantMutation.mutate(request, {
      onSuccess: (result) => setBulkResult(result),
    })
  }

  return (
    <PanelContent
      permissions={permissions}
      isLoading={isLoading}
      onRevoke={(id) => revokeMutation.mutate(id)}
      isRevoking={revokeMutation.isPending}
      onGrant={handleGrant}
      isGranting={grantMutation.isPending}
      onBulkGrant={handleBulkGrant}
      isBulkGranting={bulkGrantMutation.isPending}
      bulkResult={bulkResult}
    />
  )
}

interface PanelContentProps {
  permissions: Parameters<typeof PermissionsTable>[0]["permissions"]
  isLoading: boolean
  onRevoke: (id: string) => void
  isRevoking: boolean
  onGrant: (
    request: GrantDocumentPermissionRequest | GrantFolderPermissionRequest,
  ) => void
  isGranting: boolean
  onBulkGrant: (
    request: import("../../dtos/BulkPermissionDtos").BulkShareRequest,
  ) => void
  isBulkGranting: boolean
  bulkResult: BulkShareResult | null
}

function PanelContent({
  permissions,
  isLoading,
  onRevoke,
  isRevoking,
  onGrant,
  isGranting,
  onBulkGrant,
  isBulkGranting,
  bulkResult,
}: PanelContentProps) {
  return (
    <div className="space-y-6 py-4">
      {/* Section 1: Current Permissions */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Current Permissions</h3>
        <PermissionsTable
          permissions={permissions}
          isLoading={isLoading}
          onRevoke={onRevoke}
          isRevoking={isRevoking}
        />
      </div>

      <Separator />

      {/* Section 2: Grant New Permission */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Grant New Permission</h3>
        <GrantPermissionForm onGrant={onGrant} isPending={isGranting} />
      </div>

      <Separator />

      {/* Section 3: Bulk Grant (collapsible) */}
      <div>
        <BulkGrantForm
          onBulkGrant={onBulkGrant}
          isPending={isBulkGranting}
          lastResult={bulkResult}
        />
      </div>
    </div>
  )
}

export function PermissionPanel({
  open,
  onClose,
  type,
  targetId,
  targetName,
}: PermissionPanelProps) {
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {type === "folder" ? "📁 " : "📄 "}
            {targetName}
          </SheetTitle>
        </SheetHeader>

        {open &&
          targetId &&
          (type === "document" ? (
            <DocumentPermissionPanel
              targetId={targetId}
              targetName={targetName}
              onClose={onClose}
            />
          ) : (
            <FolderPermissionPanel
              targetId={targetId}
              targetName={targetName}
              onClose={onClose}
            />
          ))}
      </SheetContent>
    </Sheet>
  )
}
